import {
    AfterViewInit,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewChild,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Coordinates, PlacePredictionSelectEvent } from './types/go-map-types';
import { fromLatLng, toLatLngLiteral } from './helpers/go-map-helper';
import { GoMapService } from '../../services/go-map/go-map-service';
import {
    DEFAULT_GEO_CENTER,
    DEFAULT_MAP_OPTIONS,
} from './constants/go-map-constants';
import {
    catchError,
    EMPTY,
    filter,
    from,
    fromEvent,
    fromEventPattern,
    map,
    Observable,
    of,
    Subject,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs';
import { GeoCoordinateSystemService } from '../../../../../../core/services/geo-coordinate-system/geo-coordinate-system.service';

@Component({
    selector: 'app-go-map-picker',
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
    ],
    templateUrl: './go-map.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class GoMapComponent implements AfterViewInit, OnDestroy {
    @Input() start: Coordinates | null = null;
    @Output() coordsChange = new EventEmitter<Coordinates>();

    @ViewChild('mapElement', { static: true })
    mapElement!: ElementRef<HTMLDivElement>;
    @ViewChild('autoCompleteDropdownElement', { static: true })
    autoCompleteDropdownElement!: ElementRef<HTMLElement>;

    latitude: number | null = null;
    longitude: number | null = null;
    locationError: string | null = null;

    private map!: google.maps.Map;
    private marker!: google.maps.marker.AdvancedMarkerElement;
    private destroy$ = new Subject<void>();

    private readonly mapsLoader = inject(GoMapService);
    private readonly geoCoordinateSystemService = inject(
        GeoCoordinateSystemService,
    );

    constructor() {}

    ngAfterViewInit(): void {
        this.mapsLoader
            .load()
            .pipe(
                tap(() => {
                    if (!google?.maps?.places || !google?.maps?.marker) {
                        throw new Error(
                            'Google Maps Places/Marker libraries not loaded. Check loader URL.',
                        );
                    }
                }),
                switchMap(() => this.getInitialCenter()),
                map((center) => ({
                    center,
                    centerLiteral: toLatLngLiteral(center),
                })),
                tap(({ center, centerLiteral }) => {
                    this.map = new google.maps.Map(
                        this.mapElement.nativeElement,
                        {
                            ...DEFAULT_MAP_OPTIONS,
                            center: centerLiteral,
                        },
                    );

                    this.initAdvancedMarker(centerLiteral);
                    this.updateMarkerPosition(center);
                    this.addMapClickListener();
                    this.addAutocompleteListener();
                }),
                take(1),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    useMyLocation(): void {
        this.locationError = null;

        this.getCurrentLocation()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (coords) => {
                    this.map.panTo(toLatLngLiteral(coords));
                    this.map.setZoom(15);
                    this.updateMarkerPosition(coords);
                },
                error: (err: GeolocationPositionError | Error) => {
                    this.locationError =
                        'code' in err && err.code === 1
                            ? 'Location access is blocked. Enable it in your browser/site settings and try again.'
                            : 'Could not get your location.';
                },
            });
    }

    /**
     * Formats a coordinate value for display.
     * Returns an em dash (—) when the value is null or undefined,
     * otherwise rounds it to the configured geographic precision.
     *
     * @param {number | null} coordinate - The coordinate value to format.
     * @returns {number | string} The rounded coordinate or an em dash if null.
     */
    formatCoordinate(coordinate: number | null): number | string {
        return coordinate == null
            ? '—'
            : this.geoCoordinateSystemService.roundToPrecision(coordinate);
    }

    /**
     * Creates a Google Advanced Marker and places it on the current map.
     * Initializes its position to the provided center point.
     *
     * @param {google.maps.LatLngLiteral} center - Initial marker position.
     * @returns {void}
     */
    private initAdvancedMarker(center: google.maps.LatLngLiteral): void {
        const { AdvancedMarkerElement } = google.maps.marker;
        this.marker = new AdvancedMarkerElement({
            map: this.map,
            position: center,
        });
    }

    /**
     * Subscribes to map click events and updates the marker/coords when the map is clicked.
     *
     * @returns {void}
     */
    private addMapClickListener(): void {
        const clicks$ = fromEventPattern<google.maps.MapMouseEvent>(
            // add
            (handler) =>
                this.map.addListener(
                    'click',
                    handler as (event: google.maps.MapMouseEvent) => void,
                ),
            // remove
            (_handler, listener: google.maps.MapsEventListener) =>
                listener.remove(),
        );

        clicks$
            .pipe(
                map((evt) => evt?.latLng?.toJSON?.() ?? null),
                filter((loc): loc is google.maps.LatLngLiteral => Boolean(loc)),
                tap((locationLiteral) => {
                    this.updateMarkerPosition(fromLatLng(locationLiteral));
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    /**
     * Wires up the Places Autocomplete (gmp-select) event on the host element.
     *
     * @returns {void}
     */
    private addAutocompleteListener(): void {
        const host = this.autoCompleteDropdownElement.nativeElement;

        fromEvent<PlacePredictionSelectEvent>(host, 'gmp-select')
            .pipe(
                switchMap((event) => this.handlePlaceFromEvent(event)),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    /**
     * Computes the initial map center as an RxJS observable.
     * Prefers the provided value from this.start; otherwise attempts browser Geolocation,
     * and falls back to DEFAULT_GEO_CENTER if unavailable.
     *
     * @returns A single { latitude, longitude } value and completes.
     */
    private getInitialCenter(): Observable<Coordinates> {
        if (this.start) {
            return of(this.start);
        }

        return this.getCurrentLocation().pipe(
            catchError((err: GeolocationPositionError | Error) => {
                // code 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
                console.warn('Geolocation failed, using default center:', err);
                return of(DEFAULT_GEO_CENTER);
            }),
        );
    }

    private getCurrentLocation(): Observable<Coordinates> {
        return new Observable<GeolocationPosition>((subscriber) => {
            if (!('geolocation' in navigator)) {
                subscriber.error(new Error('Geolocation not supported'));
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    subscriber.next(position);
                    subscriber.complete();
                },
                (error) => subscriber.error(error),
                {
                    enableHighAccuracy: false, // faster, uses wifi/cell; plenty for a map picker
                    timeout: 15000,
                    maximumAge: 60000,
                },
            );
        }).pipe(
            map((position) => ({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
            })),
        );
    }

    /**
     * Handles selection from the Google Places Autocomplete dropdown.
     * Fetches the place's location field, recenters the map, and moves the marker.
     *
     * @param event - The selected place prediction event.
     * @returns Emits once after applying the map/marker updates, then completes. Completes without emission if no location is available or on error.
     */
    private handlePlaceFromEvent(event: PlacePredictionSelectEvent) {
        const place = event.placePrediction.toPlace();

        return from(place.fetchFields({ fields: ['location'] })).pipe(
            map(() => place.location?.toJSON() ?? null),
            filter(
                (location): location is google.maps.LatLngLiteral => !!location,
            ),
            tap((locationLiteral) => {
                this.map.panTo(locationLiteral);
                this.updateMarkerPosition(fromLatLng(locationLiteral));
            }),
            map(() => void 0),
            catchError(() => EMPTY),
        );
    }

    /**
     * Updates marker position and component state from app-level coordinates.
     * Also emits the coordsChange event for parents that consume the new value.
     *
     * @param {Coordinates} coords - The new latitude/longitude to apply.
     * @returns {void}
     */
    private updateMarkerPosition(coords: Coordinates): void {
        this.marker.position = toLatLngLiteral(coords);
        this.latitude = coords.latitude;
        this.longitude = coords.longitude;
        this.coordsChange.emit(coords); // Notify parent of coords change
    }
}
